import { useEffect, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useI18n } from "@/contexts/I18nContext";
import { 
  Menu, 
  Search, 
  User, 
  X, 
  ChevronDown,
  FileText,
  Scale,
  BookOpen,
  Shield,
  Monitor,
  Car,
  Home,
  Package,
  Globe,
  Lightbulb,
  Rocket,
  Users,
  DollarSign,
  MapPin,
  Phone,
  Briefcase
} from "lucide-react";
import logoPuntoHispano from "@/assets/logo-punto-hispano.png";
import LanguageSelector from "@/components/LanguageSelector";
import { CartButton } from "@/components/cart/CartButton";
import { ShoppingCart } from "@/components/cart/ShoppingCart";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";


// Estructura completa de navegación
const navigationStructure = {
  servicios: {
    label: "Servicios",
    path: "/servicios",
    icon: FileText,
    categories: [
      {
        title: "Tradicionales",
        items: [
          { name: "Gestoría y Administración", path: "/servicios/gestoria-administracion", icon: FileText, 
            subitems: [
              { name: "Desempleo y RAV", path: "/servicios/gestoria-administracion/desempleo" },
              { name: "Administración", path: "/servicios/gestoria-administracion/administracion" },
              { name: "CV y Documentación", path: "/servicios/gestoria-administracion/cv" },
              { name: "Traducciones", path: "/servicios/gestoria-administracion/traducciones" },
              { name: "Paquetes Especiales", path: "/servicios/gestoria-administracion/paquetes" },
            ]
          },
          { name: "Asesoría Legal", path: "/servicios/asesoria-legal", icon: Scale,
            subitems: [
              { name: "Extranjería y Permisos", path: "/servicios/asesoria-legal/extranjeria" },
              { name: "Derecho Laboral", path: "/servicios/asesoria-legal/laboral" },
              { name: "Derecho de Familia", path: "/servicios/asesoria-legal/familia" },
              { name: "Derecho Mercantil", path: "/servicios/asesoria-legal/mercantil" },
              { name: "Inmobiliario y Fiscal", path: "/servicios/asesoria-legal/inmobiliario" },
            ]
          },
          { name: "Formación en Idiomas", path: "/servicios/cursos-idiomas", icon: BookOpen,
            subitems: [
              { name: "Alemán", path: "/servicios/cursos-idiomas/aleman" },
              { name: "Inglés", path: "/servicios/cursos-idiomas/ingles" },
            ]
          },
          { name: "Seguros y Pensiones", path: "/servicios/consultoria-seguros", icon: Shield,
            subitems: [
              { name: "Seguro de Salud", path: "/servicios/consultoria-seguros/salud" },
              { name: "Vida y Pensión", path: "/servicios/consultoria-seguros/vida-pension" },
              { name: "Hogar y Vehículos", path: "/servicios/consultoria-seguros/hogar" },
              { name: "Siniestros", path: "/servicios/consultoria-seguros/siniestros" },
              { name: "Comparador", path: "/servicios/consultoria-seguros/comparador" },
            ]
          },
        ]
      },
      {
        title: "Lifestyle",
        items: [
          { name: "Alquiler de Vehículos", path: "/servicios/alquileres-vehiculos", icon: Car },
          { name: "Alquiler de Viviendas", path: "/servicios/alquileres-viviendas", icon: Home },
          { name: "Pack Relocation", path: "/servicios/pack-relocation", icon: Package,
            subitems: [
              { name: "Pack Básico", path: "/servicios/pack-relocation/basico" },
              { name: "Pack Completo", path: "/servicios/pack-relocation/completo" },
              { name: "Para Empresas", path: "/servicios/pack-relocation/empresas" },
              { name: "Pack Retorno", path: "/servicios/pack-relocation/retorno" },
            ]
          },
        ]
      },
      {
        title: "Digitales",
        items: [
          { name: "Soluciones Digitales", path: "/servicios/soluciones-digitales-avanzadas", icon: Monitor },
        ]
      }
    ]
  },
  emprende: {
    label: "Emprende",
    path: "/emprende",
    icon: Rocket,
    items: [
      { name: "Centro de Emprendimiento", path: "/emprende", icon: Rocket },
      { name: "Transmite Tus Ideas", path: "/emprende/ideas", icon: Lightbulb },
      { name: "Gana Dinero Online", path: "/emprende/gana-dinero", icon: DollarSign },
      { name: "Soluciones Digitales", path: "/emprende/soluciones-digitales", icon: Monitor },
      { name: "Colabora con Nosotros", path: "/emprende/colabora", icon: Users },
    ]
  },
  // zonas: bloque eliminado (no se renderiza en el header; en mobile se enlaza directamente a /zonas con icono Globe)

  empresa: {
    label: "Nosotros",
    path: "/sobre-nosotros",
    icon: Users,
    items: []
  }
};

// Datos de búsqueda extendidos
const searchData = [
  { name: "Gestoría y Administración", path: "/servicios/gestoria-administracion", keywords: ["gestoria", "administracion", "documentos", "tramites", "papeles", "burocracia"], category: "Servicios" },
  { name: "Asesoría Legal", path: "/servicios/asesoria-legal", keywords: ["legal", "abogado", "leyes", "contratos", "asesor", "juridico"], category: "Servicios" },
  { name: "Cursos de Idiomas", path: "/servicios/cursos-idiomas", keywords: ["idiomas", "ingles", "aleman", "frances", "cursos", "clases", "aprender"], category: "Servicios" },
  { name: "Consultoría de Seguros", path: "/servicios/consultoria-seguros", keywords: ["seguros", "seguro", "salud", "vida", "coche", "auto", "hogar", "pension"], category: "Servicios" },
  { name: "Soluciones Digitales", path: "/servicios/soluciones-digitales-avanzadas", keywords: ["digital", "web", "pagina", "app", "tecnologia", "software", "marketing", "ia", "crm"], category: "Servicios" },
  { name: "Alquiler de Vehículos", path: "/servicios/alquileres-vehiculos", keywords: ["vehiculos", "coches", "autos", "alquiler", "coche", "auto", "furgoneta", "rental", "car"], category: "Servicios" },
  { name: "Alquiler de Viviendas", path: "/servicios/alquileres-viviendas", keywords: ["viviendas", "pisos", "apartamentos", "habitacion", "habitaciones", "casa", "alquiler", "alojamiento", "piso", "flat", "room"], category: "Servicios" },
  { name: "Pack Relocation", path: "/servicios/pack-relocation", keywords: ["relocation", "mudanza", "traslado", "llegada", "suiza", "pack", "paquete"], category: "Servicios" },
  { name: "Desempleo (RAV)", path: "/servicios/gestoria-administracion/desempleo", keywords: ["desempleo", "paro", "rav", "subsidio", "trabajo", "empleo"], category: "Gestoría" },
  { name: "CV y Documentación", path: "/servicios/gestoria-administracion/cv", keywords: ["cv", "curriculum", "documentacion", "carta", "motivacion"], category: "Gestoría" },
  { name: "Traducciones", path: "/servicios/gestoria-administracion/traducciones", keywords: ["traduccion", "traducciones", "traducir", "idioma", "documento"], category: "Gestoría" },
  { name: "Acompañamientos", path: "/servicios/gestoria-administracion/acompanamientos", keywords: ["acompañamiento", "citas", "oficinas", "interprete"], category: "Gestoría" },
  { name: "Creación de Empresas", path: "/servicios/gestoria-administracion/creacion-empresas", keywords: ["empresa", "negocio", "autonomo", "sarl", "gmbh", "emprender"], category: "Gestoría" },
  { name: "Centro de Emprendimiento", path: "/emprende", keywords: ["emprende", "emprendimiento", "startup", "negocio", "digital"], category: "Emprende" },
  { name: "Transmite Tus Ideas", path: "/emprende/ideas", keywords: ["ideas", "proyecto", "propuesta", "innovacion"], category: "Emprende" },
  { name: "Gana Dinero Online", path: "/emprende/gana-dinero", keywords: ["dinero", "ingresos", "freelance", "remoto", "ganar"], category: "Emprende" },
  { name: "Colabora con Nosotros", path: "/emprende/colabora", keywords: ["colaborar", "partner", "afiliado", "socio"], category: "Emprende" },
  { name: "Servicios en Suiza", path: "/zonas/suiza", keywords: ["suiza", "zurich", "ginebra", "berna", "basel"], category: "Zonas" },
  { name: "Servicios en España", path: "/zonas/espana", keywords: ["españa", "madrid", "barcelona", "valencia", "sevilla"], category: "Zonas" },
  { name: "Servicios en Italia", path: "/zonas/italia", keywords: ["italia", "milan", "roma", "napoles"], category: "Zonas" },
  { name: "Servicios en Alemania", path: "/zonas/alemania", keywords: ["alemania", "berlin", "munich", "frankfurt"], category: "Zonas" },
  { name: "Servicios en México", path: "/zonas/mexico", keywords: ["mexico", "cdmx", "guadalajara", "monterrey"], category: "Zonas" },
  { name: "Ver Todas las Zonas", path: "/zonas", keywords: ["zonas", "paises", "ciudades", "mundial"], category: "Zonas" },
  { name: "Sobre Nosotros", path: "/sobre-nosotros", keywords: ["nosotros", "equipo", "empresa", "sobre", "mision", "valores"], category: "Empresa" },
  { name: "Contacto", path: "/contacto", keywords: ["contacto", "telefono", "email", "oficina", "cita", "whatsapp"], category: "Empresa" },
  { name: "Blog", path: "/blog", keywords: ["blog", "noticias", "articulos", "informacion"], category: "Empresa" },
];

const CinematicNavbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileExpandedSection, setMobileExpandedSection] = useState<string | null>(null);
  const [expandedSubitems, setExpandedSubitems] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { t, localizedPath } = useI18n();
  
  const isHomePage = location.pathname === '/' || location.pathname.match(/^\/(es|de|fr|it|pt|en)\/?$/);


  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

  const handleDropdownEnter = (key: string) => {
    if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
    setActiveDropdown(key);
  };

  const handleDropdownLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => { setActiveDropdown(null); setExpandedSubitems(null); }, 150);
  };

  const searchResults = searchData.filter(item => {
    if (searchQuery.length < 2) return false;
    const query = searchQuery.toLowerCase();
    return item.name.toLowerCase().includes(query) || 
           item.keywords.some(kw => kw.includes(query));
  });

  const groupedResults = searchResults.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {} as Record<string, typeof searchResults>);

  // Desktop nav link style helper
  const navLinkClass = (path: string, exactMatch = false) => {
    const active = exactMatch 
      ? (location.pathname === path || location.pathname.match(/^\/(es|de|fr|it|pt|en)\/?$/))
      : isActive(path);
    return `relative whitespace-nowrap px-2 2xl:px-3 py-2 text-[13px] 2xl:text-sm font-medium transition-colors ${
      active
        ? "text-punto"
        : "text-black/60 hover:text-black"
    }`;
  };

  return (
    <>
      <motion.nav
        initial={{ y: isHomePage ? -100 : 0, opacity: isHomePage ? 0 : 1 }}
        animate={{
          // On mobile/tablet always show the navbar so users have access to menu/login/cart.
          // On desktop home, keep the cinematic reveal-on-scroll behaviour.
          y: isHomePage ? (isScrolled ? 0 : (typeof window !== 'undefined' && window.innerWidth < 1280 ? 0 : -100)) : 0,
          opacity: isHomePage ? (isScrolled ? 1 : (typeof window !== 'undefined' && window.innerWidth < 1280 ? 1 : 0)) : 1
        }}
        transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled 
            ? "bg-white/95 backdrop-blur-2xl shadow-[0_1px_30px_rgba(0,0,0,0.08)] border-b border-black/[0.06]" 
            : "bg-white/90 backdrop-blur-xl border-b border-black/[0.04]"
        }`}
      >
        {/* Subtle top accent line */}
        <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-punto/20 to-transparent" />
        
        <div className="container mx-auto px-4">
          <div className={`flex items-center justify-between transition-all duration-500 ${
            isScrolled ? "h-14 md:h-16" : "h-16 md:h-20"
          }`}>
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 shrink-0 group relative">
              <img
                src={logoPuntoHispano}
                alt="Punto Hispano"
                className={`w-auto transition-all duration-500 ${isScrolled ? "h-8" : "h-10"}`}
                loading="eager"
              />
              {/* Logo hover glow */}
              <div className="absolute -inset-2 rounded-xl bg-punto/0 group-hover:bg-punto/5 transition-colors duration-300" />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-0 2xl:gap-0.5 flex-1 justify-center min-w-0">
              {/* Inicio */}
              <Link to={localizedPath("/")} className={navLinkClass("/", true)}>
                {t.nav.home}
                {(location.pathname === "/" || location.pathname.match(/^\/(es|de|fr|it|pt|en)\/?$/)) && (
                  <motion.div layoutId="nav-underline" className="absolute bottom-0 left-3 right-3 h-[2px] bg-punto rounded-full" />
                )}
              </Link>

              {/* Servicios Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => handleDropdownEnter('servicios')}
                onMouseLeave={handleDropdownLeave}
              >
                <Link
                  to="/servicios"
                  className={`${navLinkClass("/servicios")} flex items-center gap-1`}
                >
                  {t.nav.services}
                  <ChevronDown className={`w-3.5 h-3.5 text-black/40 transition-transform duration-200 ${activeDropdown === 'servicios' ? 'rotate-180' : ''}`} />
                  {isActive("/servicios") && (
                    <motion.div layoutId="nav-underline" className="absolute bottom-0 left-3 right-3 h-[2px] bg-punto rounded-full" />
                  )}
                </Link>
                
                <AnimatePresence>
                  {activeDropdown === 'servicios' && (
                    <motion.div
                      initial={{ opacity: 0, y: 8, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.98 }}
                      transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
                      className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[820px] bg-white/95 backdrop-blur-2xl rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.12)] border border-black/[0.06] p-6 grid grid-cols-3 gap-6"
                    >
                      {/* Top gradient accent */}
                      <div className="absolute top-0 left-8 right-8 h-[1px] bg-gradient-to-r from-transparent via-punto/20 to-transparent" />
                      
                      {navigationStructure.servicios.categories.map((category) => (
                        <div key={category.title}>
                          <h4 className="text-[10px] font-bold text-punto/60 uppercase tracking-[0.2em] mb-3 pl-3">{category.title}</h4>
                          <div className="space-y-0.5">
                            {category.items.map((item) => (
                              <div key={item.path} className="relative">
                                <div className="flex items-center">
                                  <Link
                                    to={item.path}
                                    className="flex-1 flex items-center gap-2.5 px-3 py-2 text-sm font-medium text-black/70 hover:text-black hover:bg-black/[0.04] rounded-lg transition-all duration-200 group"
                                  >
                                    <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-black/[0.04] group-hover:bg-punto/10 transition-colors duration-200">
                                      <item.icon className="w-3.5 h-3.5 text-punto/80 group-hover:text-punto" />
                                    </div>
                                    <span>{item.name}</span>
                                  </Link>
                                  {item.subitems && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setExpandedSubitems(expandedSubitems === item.path ? null : item.path);
                                      }}
                                      aria-label={`Ver subcategorías de ${item.name}`}
                                      aria-expanded={expandedSubitems === item.path}
                                      className="p-2 text-black/30 hover:text-punto transition-colors rounded-md hover:bg-black/[0.04]"
                                    >
                                       <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${expandedSubitems === item.path ? 'rotate-180' : ''}`} />
                                    </button>
                                  )}
                                </div>
                                {item.subitems && expandedSubitems === item.path && (
                                   <div className="ml-10 mt-0.5 mb-1 space-y-0 border-l border-black/[0.06] pl-3">
                                    {item.subitems.map((sub) => (
                                      <Link
                                        key={sub.path}
                                        to={sub.path}
                                        className="block px-2 py-1.5 text-xs text-black/40 hover:text-punto hover:bg-black/[0.03] rounded transition-colors"
                                      >
                                        {sub.name}
                                      </Link>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Zonas — oculto del header principal */}
              <Link to="/zonas" className={`${navLinkClass("/zonas")} hidden`}>
                {t.nav.zones}
                {isActive("/zonas") && (
                  <motion.div layoutId="nav-underline" className="absolute bottom-0 left-3 right-3 h-[2px] bg-punto rounded-full" />
                )}
              </Link>

              {/* Sobre Nosotros */}
              <Link to="/sobre-nosotros" className={navLinkClass("/sobre-nosotros")}>
                {t.nav.about}
                {(isActive("/sobre-nosotros") || isActive("/quienes-somos")) && (
                  <motion.div layoutId="nav-underline" className="absolute bottom-0 left-3 right-3 h-[2px] bg-punto rounded-full" />
                )}
              </Link>

              {/* Blog */}
              <Link to="/blog" className={navLinkClass("/blog")}>
                {t.nav.blog}
                {isActive("/blog") && (
                  <motion.div layoutId="nav-underline" className="absolute bottom-0 left-3 right-3 h-[2px] bg-punto rounded-full" />
                )}
              </Link>

              {/* Contacto — CTA pill */}
              <Link
                to="/contacto"
                className={`whitespace-nowrap px-3 2xl:px-4 py-1.5 ml-1 text-[13px] 2xl:text-sm font-semibold rounded-full transition-all duration-300 border ${
                  isActive("/contacto")
                    ? "bg-punto text-white border-punto shadow-[0_0_20px_rgba(168,28,28,0.3)]"
                    : "text-punto border-punto/40 hover:bg-punto hover:text-white hover:border-punto hover:shadow-[0_0_15px_rgba(168,28,28,0.15)]"
                }`}
              >
                {t.nav.contact}
              </Link>
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center gap-1.5">
              {/* Search Button — desktop only (mobile uses hamburger + WhatsApp CTA) */}
              <button
                onClick={() => setIsSearchOpen(true)}
                className="hidden lg:flex h-8 w-8 items-center justify-center rounded-full bg-black/[0.04] hover:bg-black/[0.08] border border-black/[0.06] hover:border-black/[0.1] transition-all duration-300 group"
                aria-label="Buscar"
              >
                <Search className="h-3.5 w-3.5 shrink-0 text-black/50 group-hover:text-black transition-colors" />
              </button>

              {/* Cart Button — desktop only */}
              <div className="hidden lg:block">
                <CartButton />
              </div>

              {/* Language Selector — desktop */}
              <div className="hidden lg:block">
                <LanguageSelector variant="compact" className="text-black/70 hover:text-black" />
              </div>

              {/* Login — desktop only (mobile users access it via hamburger menu) */}
              <Link
                to="/auth"
                className="hidden lg:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/[0.04] hover:bg-black/[0.08] border border-black/[0.06] hover:border-black/[0.1] text-black/60 hover:text-black text-[13px] 2xl:text-sm font-semibold transition-all duration-300 whitespace-nowrap"
              >
                <User className="w-3.5 h-3.5 shrink-0" />
                <span>{t.nav.access}</span>
              </Link>

              {/* Mobile-only: primary WhatsApp CTA in navbar for instant conversion */}
              <a
                href="https://wa.me/41772317288?text=Hola%2C%20necesito%20informaci%C3%B3n"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Contactar por WhatsApp"
                className="lg:hidden inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-punto text-white text-[13px] font-semibold shadow-[0_6px_20px_-6px_rgba(168,28,28,0.5)] hover:bg-punto/90 transition-all"
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/></svg>
                <span>WhatsApp</span>
              </a>




              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden h-9 w-9 flex items-center justify-center rounded-full bg-black/[0.04] hover:bg-black/[0.08] border border-black/[0.06] transition-all duration-300"
                aria-label={isMobileMenuOpen ? t.nav.closeMenu : t.nav.openMenu}
              >
                <AnimatePresence mode="wait" initial={false}>
                  {isMobileMenuOpen ? (
                    <motion.div
                      key="close"
                      initial={{ rotate: -90, opacity: 0 }}
                      animate={{ rotate: 0, opacity: 1 }}
                      exit={{ rotate: 90, opacity: 0 }}
                      transition={{ duration: 0.15 }}
                    >
                      <X className="h-5 w-5 shrink-0 text-black" />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="menu"
                      initial={{ rotate: 90, opacity: 0 }}
                      animate={{ rotate: 0, opacity: 1 }}
                      exit={{ rotate: -90, opacity: 0 }}
                      transition={{ duration: 0.15 }}
                    >
                      <Menu className="h-5 w-5 shrink-0 text-black" />
                    </motion.div>
                  )}
                </AnimatePresence>
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu — Premium Dark Slide */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ duration: 0.35, ease: [0.23, 1, 0.32, 1] }}
            className="fixed top-14 md:top-16 left-0 right-0 bottom-0 z-40 bg-white lg:hidden overflow-y-auto"
          >
            {/* Top gradient line */}
            <div className="h-[1px] bg-gradient-to-r from-transparent via-punto/20 to-transparent" />
            
            <div className="container mx-auto px-4 py-6">
              {/* Language Selector — mobile */}
              <div className="mb-6 pb-4 border-b border-black/[0.06]">
                <LanguageSelector variant="navbar" className="w-full justify-center text-black/70 !bg-black/[0.03] hover:!bg-black/[0.06] border border-black/[0.06] rounded-xl px-4 py-3" />
              </div>

              {/* Mobile Nav Links */}
              <div className="space-y-1">
                <Link
                  to="/"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3.5 text-base font-medium text-black/80 hover:text-black hover:bg-black/[0.04] rounded-xl transition-all duration-200"
                >
                  <Home className="w-4 h-4 text-punto/60" />
                  {t.nav.home}
                </Link>

                {/* Servicios Expandible */}
                <div>
                  <button
                    onClick={() => setMobileExpandedSection(mobileExpandedSection === 'servicios' ? null : 'servicios')}
                    className="w-full flex items-center justify-between px-4 py-3.5 text-base font-medium text-black/80 hover:text-black hover:bg-black/[0.04] rounded-xl transition-all duration-200"
                  >
                    <span className="flex items-center gap-3">
                      <FileText className="w-4 h-4 text-punto/60" />
                      {t.nav.services}
                    </span>
                    <ChevronDown className={`w-5 h-5 text-black/30 transition-transform duration-300 ${mobileExpandedSection === 'servicios' ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {mobileExpandedSection === 'servicios' && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="pl-4 py-2 space-y-1">
                          {navigationStructure.servicios.categories.map((cat) => (
                            <div key={cat.title} className="mb-4">
                              <p className="px-4 py-1.5 text-[10px] font-bold text-punto/60 uppercase tracking-[0.2em]">{cat.title}</p>
                              {cat.items.map((item) => (
                                <Link
                                  key={item.path}
                                  to={item.path}
                                  onClick={() => setIsMobileMenuOpen(false)}
                                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-black/60 hover:text-black hover:bg-black/[0.03] rounded-lg transition-all duration-200"
                                >
                                  <item.icon className="w-4 h-4 text-punto/50" />
                                  <span>{item.name}</span>
                                </Link>
                              ))}
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Divider */}
                <div className="h-[1px] mx-4 bg-black/[0.06]" />

                <Link
                  to="/zonas"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="hidden"
                >
                  <Globe className="w-4 h-4 text-punto/60" />
                  {t.nav.zones}
                </Link>

                <Link
                  to="/sobre-nosotros"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3.5 text-base font-medium text-black/80 hover:text-black hover:bg-black/[0.04] rounded-xl transition-all duration-200"
                >
                  <Users className="w-4 h-4 text-punto/60" />
                  {t.nav.about}
                </Link>

                <Link
                  to="/blog"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3.5 text-base font-medium text-black/80 hover:text-black hover:bg-black/[0.04] rounded-xl transition-all duration-200"
                >
                  <BookOpen className="w-4 h-4 text-punto/60" />
                  {t.nav.blog}
                </Link>

                {/* Divider */}
                <div className="h-[1px] mx-4 bg-black/[0.06]" />

                {/* Contact CTA */}
                <Link
                  to="/contacto"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center justify-center gap-2 mx-4 mt-2 px-6 py-3.5 text-base font-semibold text-white bg-punto rounded-xl hover:bg-punto/90 transition-all duration-300 shadow-[0_0_30px_rgba(168,28,28,0.2)]"
                >
                  {t.nav.contact}
                </Link>
              </div>

              {/* Mobile Bottom Actions */}
              <div className="mt-6 pt-6 border-t border-black/[0.06] flex items-center gap-3">
                <CartButton />
                <Link
                  to="/auth"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-black/[0.04] hover:bg-black/[0.08] border border-black/[0.06] text-black rounded-xl font-medium transition-all duration-300"
                >
                  <User className="w-4 h-4" />
                  {t.nav.access}
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Enhanced Search Modal */}
      <Dialog open={isSearchOpen} onOpenChange={setIsSearchOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] bg-white/95 backdrop-blur-2xl border-black/[0.08]">
          <DialogHeader>
            <DialogTitle className="text-xl text-black">{t.nav.search}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 shrink-0 text-black/30" />
              <Input
                placeholder={t.nav.searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 text-lg bg-black/[0.03] border-black/[0.06] text-black placeholder:text-black/30 focus:border-punto/40"
                autoFocus
              />
            </div>
            
            <ScrollArea className="h-[400px]">
              {searchQuery.length >= 2 && Object.keys(groupedResults).length > 0 ? (
                <div className="space-y-6">
                  {Object.entries(groupedResults).map(([category, items]) => (
                    <div key={category}>
                      <h4 className="text-[10px] font-bold text-punto/60 uppercase tracking-[0.2em] mb-2 px-2">{category}</h4>
                      <div className="space-y-1">
                        {items.map((result) => (
                          <Link
                            key={result.path}
                            to={result.path}
                            onClick={() => {
                              setIsSearchOpen(false);
                              setSearchQuery("");
                            }}
                            className="flex items-center gap-3 p-3 rounded-lg hover:bg-black/[0.04] transition-colors group"
                          >
                            <MapPin className="w-4 h-4 text-punto/50 group-hover:text-punto" />
                            <span className="font-medium text-black/70 group-hover:text-black">{result.name}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : searchQuery.length >= 2 ? (
                <div className="text-center py-12">
                  <Search className="w-12 h-12 text-black/20 mx-auto mb-4" />
                  <p className="text-black/50">{t.nav.noResults} "{searchQuery}"</p>
                  <p className="text-sm text-black/30 mt-2">{t.nav.tryOther}</p>
                </div>
              ) : (
                <div className="space-y-6 py-4">
                  <div>
                    <p className="text-sm text-black/40 mb-3 px-2">{t.nav.popularSearches}</p>
                    <div className="flex flex-wrap gap-2">
                      {["Gestoría", "Seguros", "Habitación", "Idiomas", "Legal", "Desempleo", "Relocation", "Web"].map((term) => (
                        <button
                          key={term}
                          onClick={() => setSearchQuery(term)}
                          className="px-4 py-2 text-sm rounded-full bg-black/[0.04] hover:bg-punto/10 hover:text-punto text-black/60 transition-colors border border-black/[0.06] hover:border-punto/30"
                        >
                          {term}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-sm text-black/40 mb-3 px-2">{t.nav.quickAccess}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { name: "Consulta Gratuita", path: "/contacto", icon: Phone },
                        { name: "Todas las Zonas", path: "/zonas", icon: Globe },
                        { name: "Emprende Digital", path: "/emprende", icon: Rocket },
                        { name: "Sobre Nosotros", path: "/sobre-nosotros", icon: Briefcase },
                      ].map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => {
                            setIsSearchOpen(false);
                            setSearchQuery("");
                          }}
                          className="flex items-center gap-3 p-3 rounded-xl bg-black/[0.03] hover:bg-punto/10 hover:text-punto text-black/60 transition-colors border border-black/[0.06] hover:border-punto/20"
                        >
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-punto/10 text-punto shrink-0">
                            <item.icon className="w-4 h-4" />
                          </div>
                          <span className="font-medium">{item.name}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      {/* Shopping Cart Drawer */}
      <ShoppingCart />
    </>
  );
};

export default CinematicNavbar;
